import { action, computed, thunk, thunkOn } from 'easy-peasy';
import { api } from '~/api';
import type { NotifModel } from './types';

// The selector's value for "All my solutions". Solution ids are uuids, so it
// cannot collide with one.
export const ALL_SOLUTIONS = 'all';

const notifModel: NotifModel = {
  prefs: null,
  solutionId: '',
  productsPrefs: {},

  // Under All my solutions, only the rows every solution has: the rest are
  // settings for individual solutions.
  solutionKeys: computed(
    [
      (state) => state.prefs,
      (state) => state.solutionId,
      (_, storeState) => storeState.auth.solutions,
    ],
    (prefs, solutionId, solutions) => {
      const keysOf = (sid: string) => prefs?.solutions?.[sid]?.keys ?? [];
      if (solutionId !== ALL_SOLUTIONS) return keysOf(solutionId);
      const [first = [], ...rest] = solutions.map((s) => keysOf(s.id));
      return first.filter((key) => rest.every((keys) => keys.includes(key)));
    }
  ),

  setSolutionId: action((state, id) => {
    state.solutionId = id;
  }),

  update: action((state, data) => {
    state.prefs = { ...state.prefs, ...data } as NotifModel['prefs'];
  }),

  // Merges per solution, so a save's enabled list keeps the catalogue it was
  // loaded with.
  updateProductsPrefs: action((state, data) => {
    Object.entries(data).forEach(([sid, prefs]) => {
      state.productsPrefs[sid] = { ...state.productsPrefs[sid], ...prefs };
    });
  }),

  patch: thunk(async (actions, data) => {
    const result = await api.getData(
      {
        type: 'user',
        id: 'notification-prefs',
        getResult: (x) => x,
      },
      undefined,
      {
        method: 'patch',
        data,
      }
    );
    actions.update(result);
  }),

  // enable/disable answers with that solution's enabled pids, a class save
  // with every solution it touched. A reply speaks only for the pids its save
  // covered, so it cannot undo a box whose own save is still in flight.
  patchProductPrefs: thunk(async (actions, data, { getState }) => {
    const result = await api.getData(
      {
        type: 'user',
        id: 'notification-products',
        getResult: (x) => x,
      },
      undefined,
      {
        method: 'put',
        data,
      }
    );
    const enabled: Record<string, string[]> = result.solutions ?? {
      [data.solution!]: result.products,
    };
    const { productsPrefs } = getState();
    actions.updateProductsPrefs(
      Object.fromEntries(
        Object.entries(enabled).map(([sid, products]) => {
          const current = productsPrefs[sid];
          if (!current?.catalogue) return [sid, { products }];
          const covered =
            'classes' in data
              ? current.catalogue
                  .filter((p) => p.class.slug in data.classes)
                  .map((p) => p.pid)
              : [...(data.enable ?? []), ...(data.disable ?? [])];
          return [
            sid,
            {
              products: [
                ...current.products.filter((p) => !covered.includes(p)),
                ...products.filter((p) => covered.includes(p)),
              ],
            },
          ];
        })
      )
    );
  }),

  // All my solutions is one request for every solution, which the mixed state
  // needs before it can draw anything.
  fetchProducts: thunk(async (actions, solution) => {
    const all = solution === ALL_SOLUTIONS;
    const result = await api.getData(
      {
        type: 'user',
        id: 'notification-products',
        getResult: (x) => x,
      },
      all ? undefined : { solution }
    );
    actions.updateProductsPrefs(
      all ? result.solutions : { [solution]: result }
    );
  }),

  loadProducts: thunk(
    async (actions, solution, { getState, getStoreState }) => {
      const { productsPrefs } = getState();
      const sids =
        solution === ALL_SOLUTIONS
          ? getStoreState().auth.solutions.map((s) => s.id)
          : [solution];
      if (!solution || sids.every((sid) => sid in productsPrefs)) return;
      await actions.fetchProducts(solution);
    }
  ),

  onSetSolutionId: thunkOn(
    (actions) => actions.setSolutionId,
    async (actions, target) => {
      await actions.loadProducts(target.payload);
    }
  ),
};

export default notifModel;
